<style scoped>
.headline {
  background-color: #1b2643;
  width: 100%;
  height: 60px;
  color: #ffffff;
}

.content {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}

.active_text {
  border-bottom: 2px solid #ffffff;
  height: 60px;
  font-size: 16px;
}

.bold-large-text {
  font-weight: bold;
  font-size: 18px;
}

::v-deep .v-data-table th {
  min-width: 90px !important;
}

::v-deep .v-data-table td {
  text-align: center;
  white-space: nowrap;
}

td.displayName-column {
  min-width: 150px;
  max-width: 150px;
  white-space: normal;
  overflow-wrap: break-word;
}

td.dialogId-column {
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

td.score {
  text-align: center;
}

.freeze-columns .v-data-table__wrapper {
  overflow-x: auto;
  position: relative;
}

.freeze-columns th:nth-child(-n + 4),
.freeze-columns td:nth-child(-n + 4) {
  position: relative;
  z-index: 1;
  background-color: white;
}

.freeze-columns th:nth-child(4),
.freeze-columns td:nth-child(4) {
  border-right: 2px solid #ccc;
}

::v-deep .freeze-columns .v-data-table-header th:nth-child(-n + 4) {
  position: relative;
  z-index: 1;
  background-color: white;
}
</style>

<template>
  <div>
    <div class="mt-3">
      <v-row class="headline ml-n1">
        <v-col class="active_text d-flex align-center px-5">
          <p class="mb-0">{{ $t('campaignResult.cardTitle') }}</p>
        </v-col>
      </v-row>

      <!-- Campaign not found -->
      <div class="content pa-10" v-if="error">
        <v-row>
          <v-col>
            <p class="mb-0">{{ $t('campaignResult.campaignNameError') }}</p>
          </v-col>
        </v-row>
      </div>

      <div class="content pa-10" v-else>
        <v-row>
          <v-col class="bold-large-text">{{ campaignCodeName }}</v-col>
        </v-row>

        <v-row>
          <v-col
            >{{ $t('campaignResult.lastRetrievedTime') }}: {{ lastRetrievedTime
            }}<span v-if="lastRetrievedTime"> JST</span></v-col
          >
        </v-row>

        <v-row>
          <v-col>
            <v-data-table
              dense
              multi-sort
              :headers="headers"
              :items="recalculateRows()"
              :custom-sort="customSort"
              :footer-props="{
                'items-per-page-options': [100, 300, 1000, 5000, -1],
              }"
              :loading="loading"
              :loading-text="$t('campaignResult.loadingText')"
              class="freeze-columns elevation-0"
              ref="dataTable"
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item.couponCode }}</td>
                  <td class="displayName-column">{{ item.displayName }}</td>
                  <td
                    v-if="
                      item.status === $t('campaignResult.status.analyzed') ||
                      (item.canDo && item.canDo !== '') ||
                      (item.review && item.review !== '')
                    "
                    class="dialogId-column"
                  >
                    <router-link
                      :to="`/dashboard/conversation-analysis/${item.dialogId}`"
                    >
                      {{ item.dialogId }}
                    </router-link>
                  </td>
                  <td v-else class="dialogId-column">{{ item.dialogId }}</td>
                  <td>{{ item.status }}</td>
                  <td>{{ item.startedAt }}</td>
                  <td>{{ item.completedAt }}</td>
                  <td>{{ item.duration }}</td>
                  <td>
                    {{ item.review || '' }}
                  </td>
                  <td :class="cefrStyleClass(item.overall)">
                    {{ item.overall }}
                  </td>
                  <td :class="cefrStyleClass(item.range)">
                    {{ item.range }}
                  </td>
                  <td :class="cefrStyleClass(item.accuracy)">
                    {{ item.accuracy }}
                  </td>
                  <td :class="cefrStyleClass(item.phonology)">
                    {{ item.phonology }}
                  </td>
                  <td :class="cefrStyleClass(item.fluency)">
                    {{ item.fluency }}
                  </td>
                  <td :class="cefrStyleClass(item.coherence)">
                    {{ item.coherence }}
                  </td>
                  <td :class="cefrStyleClass(item.interaction)">
                    {{ item.interaction }}
                  </td>
                  <td v-for="n in maxCanDos" :key="'canDo' + n">
                    {{ item['canDo' + n] }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "@vue/composition-api";
import dayjs from "dayjs";
import type Vue from "vue";
import { campaignService } from "@/api/campaignService";
import {
  cefrStyleClass,
  roundToTwoDecimals,
} from "@/components/utils/commonUtils";
import { getJstTime } from "@/components/utils/dateUtils";
import type { Coupon } from "@/resources/coupon";
import type { TableBody, TableHeader } from "@/resources/table";
import store from "@/store";
import { determineCampaignStatus } from "@/utils";
import { getProcessedCampaignResults } from "@/utils/getProcessedCampaignResults";

const RATIO_SORT_KEYS = new Set(["review"]);

const toSortKey = (sortKey: string, value: unknown): unknown => {
  if (typeof value !== "string") return value;
  if (RATIO_SORT_KEYS.has(sortKey)) {
    const ratio = value.match(/^(\d+(?:\.\d+)?)\/([1-9]\d*)$/);
    if (ratio) return Number(ratio[1]) / Number(ratio[2]);
  }
  return value.toLowerCase();
};

export default defineComponent({
  name: "CampaignResults",
  data() {
    return {
      headers: [] as TableHeader[],
      rows: [] as TableBody[],
      lastRetrievedTime: "",
      campaignCodeName: "",
      error: false,
      loading: true,
      coupons: [] as Coupon[],
    };
  },
  computed: {
    maxCanDos(): number {
      return Math.max(
        0,
        ...this.rows.map((row) => row.canDoScores?.length ?? 0),
      );
    },
  },
  watch: {
    "$i18n.locale"() {
      this.updateHeaders();
      this.updateStatusTexts();
      this.recalculateRows();
    },
  },
  created() {
    this.updateHeaders();
  },
  async mounted() {
    const customerId = store.state.customerId as unknown as string;
    const campaignCode = this.$route.params.campaignCode;
    const campaign = await campaignService.getByIdOrCode(campaignCode);
    this.addScrollListener();

    if (!campaign || campaign.customerId !== customerId) {
      this.error = true;
      this.loading = false;
      return;
    }

    try {
      const res = await getProcessedCampaignResults(campaign);
      this.campaignCodeName = res.campaignCodeName || "";
      this.coupons = res?.coupons;
      this.rows = res?.rows;
      this.updateHeaders();
      this.recalculateRows();
      this.loading = false;
      this.lastRetrievedTime = getJstTime(dayjs().toISOString());
    } catch (error) {
      this.loading = false;
      this.lastRetrievedTime = getJstTime(dayjs().toISOString());
      console.log(error);
      this.error = true;
    }
  },
  methods: {
    /**
     * Recalculates the rows data by checking the 'interaction' field of each row.
     * If the 'interaction' field contains '-1', it replaces the value with a localized
     * string representing 'unrated'. Otherwise, it retains the original 'interaction' value.
     *
     * @returns {Array} A new array of rows with updated 'interaction' values.
     */
    recalculateRows() {
      return this.rows.map((row) => ({
        ...row,
        interaction: row.interaction.includes("-1")
          ? String(this.$t("conversationAnalysis.unrated"))
          : row.interaction,
        ...Object.fromEntries(
          Array.from({ length: this.maxCanDos }, (_, i) => [
            `canDo${i + 1}`,
            row.canDoScores?.[i] ?? "",
          ]),
        ),
      }));
    },
    /**
     * Updates the table headers for the campaign results table.
     */
    updateHeaders() {
      this.headers = this.getHeaders();
    },

    /**
     * Custom sort function that places null values at the end of sorted results.
     */
    customSort(items: TableBody[], index: string[], isDesc: boolean[]) {
      return items.sort((a, b) => {
        for (let i = 0; i < index.length; i++) {
          const sortKey = index[i];
          const desc = isDesc[i];

          let aVal = (a as any)[sortKey];
          let bVal = (b as any)[sortKey];

          // Handle null, undefined, empty string values
          const aIsNull =
            aVal === null || aVal === undefined || aVal === "" || aVal === "-";
          const bIsNull =
            bVal === null || bVal === undefined || bVal === "" || bVal === "-";

          if (aIsNull && bIsNull) continue;
          if (aIsNull) return 1;
          if (bIsNull) return -1;

          aVal = toSortKey(sortKey, aVal);
          bVal = toSortKey(sortKey, bVal);

          let result = 0;
          if (aVal < bVal) result = -1;
          else if (aVal > bVal) result = 1;

          if (desc) result = -result;

          if (result !== 0) return result;
        }
        return 0;
      });
    },
    /**
     * Generates the table headers for the campaign results table.
     * Each header contains the text to be displayed, the value used for sorting, and whether it is sortable.
     * @returns {TableHeader[]} An array of table headers.
     */
    getHeaders(): TableHeader[] {
      return [
        {
          text: this.$t("campaignResult.tableHeader.couponCode") as string,
          value: "couponCode",
          align: "left",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.displayName") as string,
          value: "displayName",
          align: "left",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.dialogId") as string,
          value: "dialogId",
          align: "left",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.status") as string,
          value: "status",
          align: "left",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.startedAt") as string,
          value: "startedAt",
          align: "left",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.completedAt") as string,
          value: "completedAt",
          align: "left",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.duration") as string,
          value: "duration",
          align: "left",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.review") as string,
          value: "review",
          align: "center",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.overall") as string,
          value: "overall",
          align: "center",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.range") as string,
          value: "range",
          align: "center",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.accuracy") as string,
          value: "accuracy",
          align: "center",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.phonology") as string,
          value: "phonology",
          align: "center",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.fluency") as string,
          value: "fluency",
          align: "center",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.coherence") as string,
          value: "coherence",
          align: "center",
          sortable: true,
        },
        {
          text: this.$t("campaignResult.tableHeader.interaction") as string,
          value: "interaction",
          align: "center",
          sortable: true,
        },
        ...Array.from({ length: this.maxCanDos }, (_, i) => ({
          text: `${this.$t("campaignResult.tableHeader.canDo")} ${i + 1}`,
          value: `canDo${i + 1}`,
          align: "center",
          sortable: true,
        })),
      ];
    },
    cefrStyleClass,
    /**
     * Updates the status text and displayName for each row in the table.
     */
    updateStatusTexts() {
      this.rows.forEach((row) => {
        row.status = determineCampaignStatus(row.status) as string;
        // Re-translate deleted learner text to current locale
        // Check for existing translations in any language
        if (
          row.displayName === "Deleted learner" ||
          row.displayName === "削除されたユーザー"
        ) {
          row.displayName = this.$t("campaignResult.deletedLearner") as string;
        }
      });
    },
    /**
     * Adds a scroll event listener to handle fixed columns.
     */
    addScrollListener() {
      const dataTableRef = this.$refs.dataTable as Vue;

      if (dataTableRef && dataTableRef.$el) {
        const tableWrapper = dataTableRef.$el.querySelector(
          ".v-data-table__wrapper",
        ) as HTMLElement;

        if (tableWrapper) {
          tableWrapper.addEventListener("scroll", this.handleScroll);
        }
      }
    },
    /**
     * Handles the scroll event to keep fixed columns in place.
     */
    handleScroll(event: Event) {
      const tableWrapper = event.target as HTMLElement;
      const left = tableWrapper.scrollLeft;
      const fixedColumns = tableWrapper.querySelectorAll(
        "th:nth-child(-n+4), td:nth-child(-n+4)",
      );

      fixedColumns.forEach((column) => {
        (column as HTMLElement).style.transform = `translateX(${left}px)`;
      });
    },
    roundToTwoDecimals,
  },
});
</script>
